import { AnchorResolver } from '../models/layout/AnchorResolver';
import { Scene } from '../models/layout/Scene';
import { SceneService } from '../../services/SceneService';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { AnchorType, ElementAnchor } from '../models/layout/types';
import type { VideoEditor } from '../VideoEditor';

/**
 * SplitSceneCommand
 * Splits a scene into two scenes at a relative time point
 *
 * Algorithm:
 * 1. Find the scene to split
 * 2. Calculate absolute split time
 * 3. Find element at split time on the same track as scene's start anchor
 * 4. Create first scene: same start anchor, end anchor at split point
 * 5. Create second scene: start anchor at split point, same end anchor
 * 6. Remove original scene and add two new scenes
 */
export class SplitSceneCommand extends AbstractCommand {
  private sceneId: string;
  private relativeTime: number;
  private service: SceneService;
  private anchorResolver: AnchorResolver;

  private originalScene: Scene | null = null;
  private firstScene: Scene | null = null;
  private secondScene: Scene | null = null;

  constructor(editor: VideoEditor, sceneId: string, relativeTime: number) {
    super(editor);
    this.sceneId = sceneId;
    this.relativeTime = relativeTime;
    this.service = new SceneService();
    this.anchorResolver = new AnchorResolver();
  }

  getName(): string {
    return 'SplitSceneCommand';
  }

  execute(): boolean {
    // Find the scene to split
    this.originalScene =
      this.editor.timeline.scenes.find(scene => scene.id === this.sceneId) ??
      null;

    if (!this.originalScene) {
      console.error(`Scene ${this.sceneId} not found`);
      return false;
    }

    try {
      // Calculate absolute times
      const sceneStartTime = this.anchorResolver.resolve(
        this.originalScene.anchorStart,
        this.editor.timeline,
      );
      const sceneEndTime = this.anchorResolver.resolve(
        this.originalScene.anchorEnd,
        this.editor.timeline,
      );
      const splitTime = sceneStartTime + this.relativeTime;

      // Validate split time is within scene bounds
      if (splitTime <= sceneStartTime || splitTime >= sceneEndTime) {
        console.error(
          `Split time ${splitTime} is outside scene bounds [${sceneStartTime}, ${sceneEndTime}]`,
        );
        return false;
      }

      // Get track ID from start anchor
      const trackId = this.getTrackIdFromAnchor(this.originalScene.anchorStart);
      if (!trackId) {
        console.error('Could not determine track ID from scene start anchor');
        return false;
      }

      // Find element at split time on the track
      const elementAtSplit = this.editor.getElementAtTime(trackId, splitTime);
      if (!elementAtSplit) {
        console.error(
          `No element found at split time ${splitTime} on track ${trackId}`,
        );
        return false;
      }

      // Create split anchor pointing to the element at split time
      const splitAnchor: ElementAnchor = {
        elementId: elementAtSplit.id,
        relativeTime: splitTime - elementAtSplit.time,
        end: false,
      };

      // Create first scene (start -> split)
      this.firstScene = new Scene({
        id: `${this.originalScene.id}_split_1`,
        anchorStart: this.originalScene.anchorStart,
        anchorEnd: splitAnchor,
        layout: this.originalScene.layout.clone(),
      });

      // Create second scene (split -> end)
      this.secondScene = new Scene({
        id: `${this.originalScene.id}_split_2`,
        anchorStart: splitAnchor,
        anchorEnd: this.originalScene.anchorEnd,
        layout: this.originalScene.layout.clone(),
      });

      // Remove original scene
      this.service.removeScene(this.editor.timeline, this.sceneId);

      // Add new scenes
      this.service.addScene(this.editor.timeline, this.firstScene);
      this.service.addScene(this.editor.timeline, this.secondScene);

      // Validate and adjust scenes (should be minimal since split creates adjacent scenes)
      this.service.validateAndAdjustScenes(this.editor.timeline);
    } catch (error) {
      console.error('Failed to split scene:', error);
      return false;
    }

    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_ADDED, {
      sceneId: 'multiple',
      count: 2,
    });

    return true;
  }

  undo(): boolean {
    if (!this.originalScene || !this.firstScene || !this.secondScene) {
      return false;
    }

    try {
      // Remove split scenes
      this.service.removeScene(this.editor.timeline, this.firstScene.id);
      this.service.removeScene(this.editor.timeline, this.secondScene.id);

      // Restore original scene
      this.service.addScene(this.editor.timeline, this.originalScene);
    } catch (error) {
      console.error('Failed to undo scene split:', error);
      return false;
    }

    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_ADDED, {
      sceneId: this.originalScene.id,
      templateId: this.originalScene.layout.layoutTemplateId,
    });

    return true;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    return `Split scene ${this.sceneId} at ${this.relativeTime.toFixed(2)}s`;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  /**
   * Extracts track ID from an anchor
   */
  private getTrackIdFromAnchor(anchor: AnchorType): string | null {
    if (typeof anchor === 'string') {
      return null; // clip-start/clip-end don't have track
    }

    const elementAnchor = anchor as ElementAnchor;
    const element = this.editor.timeline.getElement(elementAnchor.elementId);

    if (!element || element.track === null) {
      return null;
    }

    return element.track.toString();
  }
}
